import {NEW_ID} from '../../../../common/constants/index.js'
import ButtonPrimary from '../../../../common/components/Button/ButtonPrimary.js'
import ButtonSecondary from '../../../../common/components/Button/ButtonSecondary.js'
import PostageBalance from './PostageBalance.js'
import Nickname from './Nickname.js'
import ShippingMethods from './ShippingMethods.js'
import {
  formSelector,
  getState,
  onlyIfAutoForm,
  updateForm,
  useSelector,
} from '../../../../store.js'
import {isPresent} from '../../../../common/utils.js'
import verde from '../../../../common/verde.js'
import {setShipper} from '../../../../data/shippers.js'
import api from '../../../../common/api.js'
import Select from '../../Common/Select.js'
import {
  baseSaveParamsSelector,
  baseShipperFormErrorsSelector,
  baseShipperInitialFormSelector,
  goToShipperSettings,
} from '../ShippersFunctions.js'
import {PITNEY_MERCHANT} from '../../../../common/constants/ShipperNames.js'
import TextInput from '../../Common/TextInput.js'

export function setUpShipperForm({shipper, formName}) {
  return {
    formName,
    initialForm: {
      ...initialFormSelector(getState(), {shipper}),
      errorsSelector,
    },
  }
}

export function errorsSelector(state, {formName}) {
  const errors = baseShipperFormErrorsSelector(state, {formName})
  const form = formSelector(state, {
    formName,
  })

  if (form.nsaType && !isPresent(form.carrier_id)) {
    errors.carrier_id = 'Carrier ID is required'
    errors.preventSave = true
    errors.hasAuthErrors = true
  }

  if (form.id !== NEW_ID && !form.showAuthPanel) {
    return errors
  }

  if (!isPresent(form.username)) {
    errors.username = 'Username is required'
    errors.preventSave = true
    errors.hasAuthErrors = true
  }

  if (!isPresent(form.password)) {
    errors.password = 'Password is required'
    errors.preventSave = true
    errors.hasAuthErrors = true
  }

  return errors
}

export function initialFormSelector(state, {shipper}) {
  const vendorConfig = shipper?.vendor_config || {}
  const nsaType =
    vendorConfig.domestic_carrier_id && vendorConfig.international_carrier_id
      ? 'both'
      : vendorConfig.domestic_carrier_id
        ? 'domestic'
        : vendorConfig.international_carrier_id
          ? 'international'
          : ''

  return {
    ...baseShipperInitialFormSelector(state, {
      shipper,
      vendor: PITNEY_MERCHANT,
    }),
    username: '',
    password: '',
    nsaType,
    carrier_id:
      nsaType === 'domestic'
        ? vendorConfig.domestic_carrier_id
        : nsaType === 'international'
          ? vendorConfig.international_carrier_id
          : nsaType
            ? vendorConfig.domestic_carrier_id ||
              vendorConfig.international_carrier_id
            : '',
    pitney_merchant_use_ordoro_developer_keys:
      !!vendorConfig.pitney_merchant_use_ordoro_developer_keys,
    serverError: null,
    showAuthPanel: false,
  }
}

function pitneyMerchantParamsSelector(state, {formName}) {
  let [shipperID, params] = baseSaveParamsSelector(getState(), {formName})
  const form = formSelector(getState(), {formName})

  params = {...params, ...getCarrierIDParams(form)}

  return [shipperID, params]
}

function getCarrierIDParams(form) {
  const params = {}

  const {nsaType, carrier_id, pitney_merchant_use_ordoro_developer_keys} = form

  if (pitney_merchant_use_ordoro_developer_keys) {
    return params
  }

  if (nsaType === 'domestic') {
    params.domestic_carrier_id = carrier_id
    params.international_carrier_id = null
  } else if (nsaType === 'international') {
    params.domestic_carrier_id = null
    params.international_carrier_id = carrier_id
  } else if (nsaType === 'both') {
    params.domestic_carrier_id = carrier_id
    params.international_carrier_id = carrier_id
  } else {
    params.domestic_carrier_id = null
    params.international_carrier_id = null
  }

  return params
}

export async function saveShipperPitneyMerchant(formName) {
  const [shipperID, params] = pitneyMerchantParamsSelector(getState(), {
    formName,
  })

  if (shipperID === NEW_ID) {
    return
  }

  updateForm(formName, {isSaving: true})

  try {
    const {json} = await api.put(`/shipper/${shipperID}/`, params)

    goToShipperSettings()

    setShipper(json)
  } catch (err) {
    updateForm(formName, {
      serverError: err.error_message || err.message,
    })
  } finally {
    updateForm(formName, {isSaving: false})
  }
}

export async function authShipperPitneyMerchant(formName) {
  const form = formSelector(getState(), {formName})
  const shipperID = form.id
  const {username, password, pitney_merchant_use_ordoro_developer_keys} = form

  const params = {
    shipperID: shipperID === NEW_ID ? undefined : shipperID,
    username,
    password,
    pitney_merchant_use_ordoro_developer_keys,
    ...(shipperID === NEW_ID ? getCarrierIDParams(form) : {}),
  }

  updateForm(formName, {isSaving: true, serverError: null})

  try {
    const {json} = await verde.post('/pitney/auth', params)

    goToShipperSettings()

    setShipper(json)
  } catch (err) {
    updateForm(formName, {
      isSaving: false,
      serverError: err.error_message || err.message,
    })
  }
}

function NSAFormElements({formName, form}) {
  const errors = useSelector((state) => form.errorsSelector(state, {formName}))

  if (form.pitney_merchant_use_ordoro_developer_keys) {
    return null
  }

  return (
    <>
      <li className="form-list-item bigger margin-top-20 margin-bottom-20">
        <Select
          label="Negotiated Service Agreement"
          id="nsa_type"
          value={form.nsaType}
          onChange={(event) =>
            updateForm(formName, {nsaType: event.target.value})
          }
        >
          <option value="">None</option>
          <option value="domestic">Domestic</option>
          <option value="international">International</option>
          <option value="both">Both</option>
        </Select>
      </li>
      {form.nsaType && (
        <li className="form-list-item bigger margin-bottom-20">
          <TextInput
            label="Carrier ID"
            name="carrier_id"
            value={form.carrier_id}
            onChange={(event) =>
              updateForm(formName, {
                carrier_id: event.target.value,
                hasChanged_carrier_id: true,
              })
            }
            errorMessage={form.hasChanged_carrier_id && errors.carrier_id}
            required
          />
        </li>
      )}
    </>
  )
}

function PitneyMerchantAuthForm({formName, form}) {
  const errors = useSelector((state) => form.errorsSelector(state, {formName}))

  return (
    <form
      className="margin-top-20"
      onSubmit={(event) => {
        event.preventDefault()

        authShipperPitneyMerchant(formName)
      }}
    >
      <ul className="form-list medium-5 margin-bottom-0">
        <li className="form-list-item bigger margin-bottom-20">
          <TextInput
            label="Username"
            name="pm_username"
            value={form.username}
            onChange={(event) =>
              updateForm(formName, {
                username: event.target.value,
                hasChanged_username: true,
              })
            }
            errorMessage={form.hasChanged_username && errors.username}
            required
          />
        </li>
        <li className="form-list-item bigger margin-bottom-20">
          <TextInput
            type="password"
            label="Client Facility ID"
            name="pm_password"
            value={form.password}
            onChange={(event) =>
              updateForm(formName, {
                password: event.target.value,
                hasChanged_password: true,
              })
            }
            errorMessage={form.hasChanged_password && errors.password}
            required
          />
        </li>
        <li className="form-list-item bigger margin-bottom-20">
          <label htmlFor="pitney_merchant_use_ordoro_developer_keys_no">
            Referred by
          </label>
          <div className="flex wrap--label-unbold margin-top-10">
            <div className="margin-right-25">
              <label htmlFor="pitney_merchant_use_ordoro_developer_keys_no">
                <input
                  type="radio"
                  className="margin-bottom-0 margin-right-5 v-align-middle"
                  id="pitney_merchant_use_ordoro_developer_keys_no"
                  name="pitney_merchant_use_ordoro_developer_keys"
                  checked={!form.pitney_merchant_use_ordoro_developer_keys}
                  onChange={() =>
                    updateForm(formName, {
                      pitney_merchant_use_ordoro_developer_keys: false,
                    })
                  }
                />
                <span className="v-align-middle">Pitney Bowes</span>
              </label>
            </div>
            <div>
              <label htmlFor="pitney_merchant_use_ordoro_developer_keys_yes">
                <input
                  type="radio"
                  className="margin-bottom-0 margin-right-5 v-align-middle"
                  id="pitney_merchant_use_ordoro_developer_keys_yes"
                  name="pitney_merchant_use_ordoro_developer_keys"
                  checked={form.pitney_merchant_use_ordoro_developer_keys}
                  onChange={() =>
                    updateForm(formName, {
                      pitney_merchant_use_ordoro_developer_keys: true,
                    })
                  }
                />
                <span className="v-align-middle">Ordoro</span>
              </label>
            </div>
          </div>
        </li>
        {form.id === NEW_ID && (
          <NSAFormElements formName={formName} form={form} />
        )}
        {form.serverError && (
          <div className="error error-message margin-bottom-20">
            {form.serverError}
          </div>
        )}
        <li className="list__item list__item--no-style">
          <ButtonPrimary
            type="submit"
            isLoading={form.isSaving}
            isDisabled={errors.hasAuthErrors}
          >
            {form.id !== NEW_ID ? 'Reauthorize' : 'Authorize'}
          </ButtonPrimary>
          <ButtonSecondary
            onClick={() => {
              goToShipperSettings()
            }}
          >
            Cancel
          </ButtonSecondary>
        </li>
      </ul>
    </form>
  )
}

function PitneyMerchantForm({formName, form}) {
  const errors = useSelector((state) => form.errorsSelector(state, {formName}))

  if (form.id !== NEW_ID) {
    return (
      <div className="settings-list-item-form-wrap settings-list-item-form-usps clear-both">
        <fieldset>
          <ul className="form-list medium-5 columns margin-bottom-0">
            <Nickname formName={formName} />
            <NSAFormElements formName={formName} form={form} />
            <ShippingMethods formName={formName} />
            <li className="list__item list__item--no-style divider--bottom padding-bottom-20">
              <ButtonPrimary
                className="btn--primary-alt"
                isLoading={form.isSaving}
                isDisabled={errors.preventSave}
                onClick={() => saveShipperPitneyMerchant(formName)}
              >
                Save
              </ButtonPrimary>
            </li>
          </ul>
        </fieldset>
        <fieldset className="margin-bottom-0">
          <div className="medium-12 columns">
            <PostageBalance shipperID={form.id} />
            {form.showAuthPanel ? (
              <PitneyMerchantAuthForm formName={formName} form={form} />
            ) : (
              <ButtonPrimary
                onClick={() => updateForm(formName, {showAuthPanel: true})}
                className="margin-top-20"
              >
                Reauthorize
              </ButtonPrimary>
            )}
          </div>
        </fieldset>
      </div>
    )
  }

  return (
    <div className="medium-12 columns">
      <PitneyMerchantAuthForm formName={formName} form={form} />
    </div>
  )
}

export default onlyIfAutoForm(PitneyMerchantForm, setUpShipperForm)
